import { TableScroll } from '@web/components/table-scroll.tsx';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminPublicDemoApi, type AdminDemoInviteRow } from '../../api/admin.ts';
import { Alert, Button, Pagination, Select, TextInput } from '../../components/ui.tsx';
import { formatNumericInstant } from '../../i18n/datetime.ts';

/**
 * Invitation links — a reusable link an operator hands to one person, which
 * takes them straight into the shared demo without the /try form.
 *
 * The link is shown ONCE, right after it is made: the server keeps only its
 * hash, the same as every other credential here. The list below says what
 * became of each one and lets a working one be withdrawn.
 */

const EXPIRY_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: '1', label: '1 day' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

const PAGE_SIZE = 50;

const STATUS_LABEL: Record<AdminDemoInviteRow['status'], string> = {
  open: 'not used yet',
  used: 'opened',
  revoked: 'withdrawn',
  expired: 'expired',
};

export function DemoInvitesSection() {
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [note, setNote] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [created, setCreated] = useState<{ url: string; expiresAt: string | null } | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const invites = useQuery({
    queryKey: ['admin', 'public-demo', 'invites', { page }],
    queryFn: ({ signal }) => adminPublicDemoApi.invites({ limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }, signal),
  });
  const currentInvites = invites.isSuccess && !invites.isFetching ? invites.data : undefined;

  const create = useMutation({
    mutationFn: () =>
      adminPublicDemoApi.createInvite({
        fullName: fullName.trim(),
        email: email.trim(),
        companyName: companyName.trim(),
        note: note.trim() || null,
        expiresInDays: expiresInDays ? Number(expiresInDays) : null,
      }),
    onSuccess: (result) => {
      setCreated({ url: result.url, expiresAt: result.expiresAt });
      setCopied(false);
      setError(null);
      setFullName('');
      setEmail('');
      setCompanyName('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'public-demo', 'invites'] });
    },
    onError: (failure: Error) => setError(failure.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => adminPublicDemoApi.revokeInvite(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'public-demo', 'invites'] }),
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <section className="card mt-8">
      <h2 className="font-semibold">Invitation links</h2>
      <p className="mt-1 text-xs text-[color:var(--color-muted)]">
        A link that takes one person straight into the demo, without the /try form. A link keeps working, as often as it
        is opened, until it expires or you withdraw it. It still works while the door is closed.
      </p>

      {error ? (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {invites.isFetching ? <p className="mt-4 text-sm">Loading invitation links…</p> : null}
      {invites.isError ? (
        <div className="mt-4">
          <Alert tone="error" title="The invitation links could not be loaded">
            <Button variant="ghost" onClick={() => void invites.refetch()}>
              Try again
            </Button>
          </Alert>
        </div>
      ) : null}

      {created ? (
        <div className="mt-4">
          <Alert tone="success" title="Invitation link ready">
            <p className="text-xs">
              Copy it now — it is only shown once.{' '}
              {created.expiresAt
                ? `It stops working on ${formatNumericInstant(created.expiresAt)}.`
                : 'It never expires.'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <code className="block max-w-full break-all text-xs" data-testid="demo-invite-url">
                {created.url}
              </code>
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard?.writeText(created.url).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}

      <form
        className="mt-4 grid gap-4 sm:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          // The button is disabled while a link is being made, but Enter still
          // submits the form — and every extra submission would mint a live
          // link that is never shown and cannot be recovered.
          if (!create.isPending && fullName.trim() && email.trim()) create.mutate();
        }}
      >
        <TextInput label="Name" value={fullName} onChange={(event) => setFullName(event.target.value)} />
        <TextInput
          label="Email"
          value={email}
          placeholder="someone@their-company.com"
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextInput label="Company" value={companyName} onChange={(event) => setCompanyName(event.target.value)} />
        <Select
          label="Expires"
          value={expiresInDays}
          options={EXPIRY_OPTIONS}
          onChange={(event) => setExpiresInDays(event.target.value)}
        />
        <TextInput label="Note (only you see this)" value={note} onChange={(event) => setNote(event.target.value)} />
        <div className="flex items-end">
          <div className="mb-4">
            <Button type="submit" disabled={create.isPending || !fullName.trim() || !email.trim()}>
              {create.isPending ? 'Creating…' : 'Create link'}
            </Button>
          </div>
        </div>
      </form>

      {currentInvites && currentInvites.invites.length > 0 ? (
        <TableScroll aria-label="Demo invitations" className="overflow-x-auto">
          <table className="data-table w-full min-w-max text-sm">
            <thead>
              <tr className="border-b border-[color:var(--color-line)] text-left">
                <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                  Invitee
                </th>
                <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                  Status
                </th>
                <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                  Expires
                </th>
                <th scope="col" className="py-2 pr-4 whitespace-nowrap">
                  Created
                </th>
                <th scope="col" className="py-2 pr-4" />
              </tr>
            </thead>
            <tbody>
              {currentInvites.invites.map((invite) => (
                <tr key={invite.id} className="border-b border-[color:var(--color-line)] align-top">
                  <td className="py-3 pr-4">
                    <span className="block max-w-[18rem] table-text font-semibold">{invite.fullName}</span>
                    <span className="block max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                      {invite.email}
                      {invite.companyName ? ` · ${invite.companyName}` : ''}
                    </span>
                    {invite.note ? (
                      <span className="block max-w-[18rem] table-text text-xs text-[color:var(--color-muted)]">
                        {invite.note}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    {STATUS_LABEL[invite.status]}
                    {invite.redeemedAt ? (
                      <span className="block text-xs text-[color:var(--color-muted)]">
                        {formatNumericInstant(invite.redeemedAt)}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    {invite.expiresAt ? formatNumericInstant(invite.expiresAt) : 'never'}
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">
                    {formatNumericInstant(invite.createdAt)}
                    <span className="block text-xs text-[color:var(--color-muted)]">{invite.createdBy}</span>
                  </td>
                  <td className="py-3 pr-4 text-right whitespace-nowrap">
                    {invite.status === 'open' || invite.status === 'used' ? (
                      <Button variant="secondary" disabled={revoke.isPending} onClick={() => revoke.mutate(invite.id)}>
                        Withdraw
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      ) : null}

      {/* Mounted whenever the total is known, not only when it spans pages:
        `Pagination` walks a reader back when `page > totalPages`, and that
        correction must still run after the sweep shrinks the list. */}
      {currentInvites && (currentInvites.total > PAGE_SIZE || page > 1) ? (
        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(currentInvites.total / PAGE_SIZE))}
          onPageChange={setPage}
        />
      ) : null}
    </section>
  );
}
